// Helpers for schema `datetime` fields.
//
// Stored format: ISO 8601 UTC with a Z suffix, e.g. "2026-07-14T15:30:00Z".
// The datetime-local input edits the UTC wall-clock time directly — what the
// user types is what gets stored (no local-timezone conversion on input).
// Strings without a timezone suffix (older templates, datetime-local values)
// are treated as UTC when parsed.

const TZ_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;

export const parseUtcDateTime = (value: unknown): Date | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();
  const normalized = TZ_SUFFIX.test(trimmed) ? trimmed : `${trimmed}Z`;
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

// "2026-07-14T15:30:00Z" -> "2026-07-14T15:30" for a datetime-local input.
export const utcIsoToInputValue = (value: unknown): string => {
  const date = parseUtcDateTime(value);
  return date ? date.toISOString().slice(0, 16) : '';
};

// "2026-07-14T15:30" (datetime-local, read as UTC) -> "2026-07-14T15:30:00Z".
// Returns '' for empty/unparseable input so clearing the picker clears the value.
export const inputValueToUtcIso = (inputValue: string): string => {
  const date = parseUtcDateTime(inputValue);
  return date ? `${date.toISOString().slice(0, 19)}Z` : '';
};

// Current time as a stored UTC ISO string (minute precision).
export const nowUtcIso = (): string => `${new Date().toISOString().slice(0, 16)}:00Z`;

// Human-readable local-time equivalent of a stored UTC value,
// e.g. "Jul 14, 2026, 9:30 AM (America/Edmonton)". '' if unparseable.
export const formatUtcAsLocal = (value: unknown): string => {
  const date = parseUtcDateTime(value);
  if (!date) return '';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatted = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone
  }).format(date);
  return `${formatted} (${timeZone})`;
};
